import React from "react";
import { displayWeight } from "@/lib/utils";
import { MateExerciseCard } from "@/components/mates/MateExerciseCard";
import { useEffectiveUser } from "@/hooks/useEffectiveUser";
import { WorkoutModelJsonResource } from "@/types/workoutTypes";
import { SetModelJsonResource } from "@/types/setTypes";
import { WeightIcon } from "lucide-react";
import {
  displayLocalDateHour,
  formatRelativeTimeWithPrefix,
} from "@/lib/dateUtils";

interface MateWorkoutCardProps {
  workout: WorkoutModelJsonResource;
  sets: SetModelJsonResource[];
}

/**
 * Read-only workout card for displaying mate's workouts
 * Similar to OneWorkoutCard but without editing capabilities
 */
export function MateWorkoutCard({ workout, sets }: MateWorkoutCardProps) {
  const { user, isLoading } = useEffectiveUser();

  if (isLoading) {
    return null;
  }

  return (
    <div className="mb-10">
      {/* Workout title - read-only */}
      <h3 className="text-lg font-medium mb-2 text-tx-default">
        {displayLocalDateHour(workout.start_at)}
      </h3>

      <div className="p-6 rounded-2xl shadow-xs bg-bg-light border border-bd-overlay">
        <div className="flex flex-col w-full">
          {/* Total volume row */}
          <div className="flex justify-center items-center mb-3 gap-2 bg-bg-overlay rounded-sm  px-2 py-1">
            <span className="flex items-center text-base  text-tx-default">
              <WeightIcon className="w-4 h-4 mr-2" />
              <span className="mr-1">Volume:</span>
              {displayWeight(
                workout.volume_lb ?? null,
                workout.volume_kg ?? null,
                user?.preferred_weight_unit ?? "lb"
              )}
            </span>
          </div>

          {/* Exercise list */}
          <div className="flex flex-col py-2 gap-4">
            {workout.exercises?.map((exercise) => (
              <MateExerciseCard
                key={exercise.nano_id}
                exercise={exercise}
                workout={workout}
                sets={sets}
                user={user}
              />
            ))}

            {/* Cover the bottom of the last exercise's vertical line */}
            <div className="relative -mt-3 bg-bg-light">&nbsp;</div>
          </div>
        </div>
      </div>
    </div>
  );
}
